import { useState } from "react";
import { Bot, CircleHelp, ExternalLink, Send, ShieldQuestion, User } from "lucide-react";

import type { Approval, ChatMessage } from "@/lib/integrations/api";
import { when } from "@/lib/integrations/words";

import { AnswerCard } from "./AnswerCard";
import { Banner, SkeletonRows } from "./Banner";
import { Button, Textarea } from "./controls";
import styles from "./integrations.module.css";

function options(approval: Approval): string[] {
  const raw = approval.proposed_change?.options;
  return Array.isArray(raw) ? raw.map(String) : [];
}

function isQuestion(approval: Approval): boolean {
  return approval.proposed_change?.kind === "question";
}

/**
 * Scout's agent: what it said, its questions with a reply box, and the changes
 * it wants approved.
 */
export function AgentPanel({
  messages,
  approvals,
  loading,
  error,
  busy,
  onReply,
  onDecide,
  onRetry,
  scoutUrl,
  stuck,
}: {
  messages: ChatMessage[] | undefined;
  approvals: Approval[] | undefined;
  loading: boolean;
  error?: string | null;
  busy: string | null;
  onReply: (text: string) => void;
  onDecide: (approval: Approval, decision: { approved?: boolean; answer?: string }) => void;
  onRetry: () => void;
  scoutUrl: string;
  /** Scout says the job needs attention. */
  stuck?: string | null;
}) {
  const [reply, setReply] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  return (
    <div className={`${styles.col} ${styles.colTight}`}>
      {error && (
        <Banner
          tone="warning"
          title="Scout is not answering."
          actions={
            <Button onClick={onRetry} disabled={loading}>
              Try again
            </Button>
          }
        >
          {error}
        </Banner>
      )}
      {stuck && (
        <Banner
          tone="warning"
          title="Scout could not build this scraper."
          actions={
            <a href={scoutUrl} target="_blank" rel="noreferrer">
              Open in Scout <ExternalLink size={13} aria-hidden />
            </a>
          }
        >
          {stuck} Give more notes below, or open it in Scout.
        </Banner>
      )}
      {(approvals ?? []).map((approval) =>
        isQuestion(approval) ? (
          <AnswerCard
            key={approval.id}
            tone="info"
            icon={<CircleHelp size={16} aria-hidden />}
            title={`Scout asks: ${approval.question}`}
            meta={when(approval.created_at)}
            actions={
              <>
                {options(approval).map((option) => (
                  <Button key={option} disabled={busy !== null} onClick={() => onDecide(approval, { answer: option })}>
                    {option}
                  </Button>
                ))}
                <Textarea
                  aria-label="Your own answer"
                  rows={1}
                  style={{ height: "auto", minHeight: "var(--h-control)", flex: "1 1 14rem", paddingTop: 6 }}
                  value={answers[approval.id] ?? ""}
                  placeholder="Reply in your own words"
                  onChange={(event) => setAnswers({ ...answers, [approval.id]: event.target.value })}
                />
                <Button
                  tone="primary"
                  disabled={busy !== null || !(answers[approval.id] ?? "").trim()}
                  onClick={() => onDecide(approval, { answer: (answers[approval.id] ?? "").trim() })}
                >
                  Reply
                </Button>
              </>
            }
          />
        ) : (
          <AnswerCard
            key={approval.id}
            tone="warning"
            icon={<ShieldQuestion size={16} aria-hidden />}
            title={`Scout wants to change the scraper: ${approval.question}`}
            meta={when(approval.created_at)}
            actions={
              <>
                <Button tone="primary" disabled={busy !== null} onClick={() => onDecide(approval, { approved: true })}>
                  Approve
                </Button>
                <Button disabled={busy !== null} onClick={() => onDecide(approval, { approved: false })}>
                  Decline
                </Button>
                <a href={scoutUrl} target="_blank" rel="noreferrer">
                  Open in Scout <ExternalLink size={13} aria-hidden />
                </a>
              </>
            }
          />
        ),
      )}
      <div className={`${styles.col} ${styles.colTight}`}>
        <div className={styles.inline}>
          <strong>Conversation</strong>
          <span className={styles.faint}>Scout’s agent keeps working when you leave this page.</span>
        </div>
        {loading && !messages ? (
          <SkeletonRows rows={3} columns={1} label="Reading the conversation" />
        ) : (
          <div className={styles.convo} aria-live="polite" aria-label="Conversation with Scout’s agent">
            {(messages ?? []).length === 0 ? (
              <p className={styles.faint} style={{ margin: 0 }}>
                Nothing said yet. Scout’s agent writes here as it studies the site.
              </p>
            ) : (
              (messages ?? []).map((message) => {
                const mine = message.sender === "user";
                return (
                  <div key={message.id} className={mine ? `${styles.bubbleRow} ${styles.bubbleRowMine}` : styles.bubbleRow}>
                    <span className={styles.avatar} aria-hidden>
                      {mine ? <User size={16} /> : <Bot size={16} />}
                    </span>
                    <div className={styles.bubble}>
                      <span className={styles.bubbleWho}>
                        <strong>{mine ? "You" : "Scout’s agent"}</strong>
                        <time dateTime={message.created_at}>{when(message.created_at)}</time>
                      </span>
                      <span className={styles.bubbleText}>{message.content}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
      <div className={styles.composer}>
        <Textarea
          aria-label="Message for Scout's agent"
          rows={2}
          style={{ height: "auto", flex: "1 1 20rem", paddingTop: 6 }}
          value={reply}
          placeholder="Tell Scout's agent more, e.g. one row per match"
          onChange={(event) => setReply(event.target.value)}
        />
        <Button
          tone="primary"
          disabled={busy !== null || !reply.trim()}
          onClick={() => {
            onReply(reply.trim());
            setReply("");
          }}
        >
          <Send size={15} aria-hidden />
          {busy === "reply" ? "Sending…" : "Send"}
        </Button>
        <a href={scoutUrl} target="_blank" rel="noreferrer">
          Open in Scout <ExternalLink size={13} aria-hidden />
        </a>
      </div>
    </div>
  );
}
